/* eslint-disable unicorn/prefer-regexp-test */
import type { PayloadAction, UnknownAction } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

import type {
  CustomerPerson,
  ICartContactInfo,
  ICartLayout,
  PurchaseConfirmation,
} from "@/api/interfaces/cartLayouts";
import type { IInitializePaymentResponse } from "@/api/interfaces/paymentLayouts";
import type { IUserLayout } from "@/api/interfaces/userLayouts";

import type { ApiRequestStatusMachine } from "./apiRequestStatusMachine";
import { hasData } from "./apiRequestStatusMachine";
import { setFormContactInfo } from "./contactInfo";
import { initializePayment } from "./payment";
import {
  bindContactInfo,
  bundleShoppingCart,
  initializeDiningCart,
  refreshCart,
  removeCustomerFromCart,
  unbundleShoppingCart,
  updateCustomerCart,
  updateTheatreResource,
} from "./shoppingCart";
import type { RootState } from "./slices";
import { logInWithID, setUser } from "./user";

const REDACTED = "***";

const partial = (value: string | null | undefined): string => {
  if (!value || value.length <= 3) return REDACTED;
  return `${REDACTED}${value.slice(-3)}`;
};

const initial = (value: string | null | undefined): string => {
  if (!value) return REDACTED;
  return `${value[0]}.`;
};

const email = (value: string | null | undefined): string => {
  if (!value) return REDACTED;
  const atIndex = value.indexOf("@");
  if (atIndex <= 0) return partial(value);
  return `${value.slice(0, Math.min(2, atIndex))}${REDACTED}${value.slice(atIndex)}`;
};

const redactContactInfo = (
  info: ICartContactInfo | null | undefined,
): ICartContactInfo | null => {
  if (!info) return null;
  return {
    ...info,
    Name: initial(info.Name),
    Email: email(info.Email),
    Phone: partial(info.Phone),
  };
};

const redactCustomerPerson = (
  person: CustomerPerson | null | undefined,
): CustomerPerson | null | undefined => {
  if (!person) return person;
  return {
    ...person,
    FirstName: initial(person.FirstName),
    LastName: initial(person.LastName),
    Email: email(person.Email),
    Mobile: partial(person.Mobile),
  };
};

const redactPurchaseConfirmation = (
  confirmation: PurchaseConfirmation | null | undefined,
): PurchaseConfirmation | null | undefined => {
  if (!confirmation) return confirmation;
  return {
    ...confirmation,
    Email: email(confirmation.Email),
    Address: partial(confirmation.Address),
  };
};

const redactCartLayout = (cart: ICartLayout): ICartLayout => ({
  ...cart,
  ContactInfo: redactContactInfo(cart.ContactInfo),
  CustomerPerson: redactCustomerPerson(cart.CustomerPerson)!,
  PurchaseConfirmation: redactPurchaseConfirmation(cart.PurchaseConfirmation)!,
  Lines: cart.Lines?.map((line) => ({
    ...line,
    noteToKitchen: line.noteToKitchen ? REDACTED : undefined,
  })),
});

const redactCartLayoutPayload = (
  action: PayloadAction<ICartLayout>,
): PayloadAction<ICartLayout> => ({
  ...action,
  payload: redactCartLayout(action.payload),
});

const redactUserLayout = (user: IUserLayout): IUserLayout => ({
  ...user,
  FirstName: initial(user.FirstName),
  LastName: initial(user.LastName),
  Email: email(user.Email),
  Phone: partial(user.Phone),
  GUID: partial(user.GUID),
});

const redactUserLayoutPayload = (
  action: PayloadAction<IUserLayout>,
): PayloadAction<IUserLayout> => ({
  ...action,
  payload: redactUserLayout(action.payload),
});

const redactCartStateMachine = (
  machine: ApiRequestStatusMachine<ICartLayout>,
): ApiRequestStatusMachine<ICartLayout> => {
  if (!hasData(machine)) return machine;
  return {
    ...machine,
    state: redactCartLayout(machine.state),
  };
};

const redactPaymentResponse = (
  response: IInitializePaymentResponse,
): IInitializePaymentResponse => ({
  ...response,
  remoteShoppingCartGuid: partial(response.remoteShoppingCartGuid),
  shortCode: partial(response.shortCode),
});

type BindContactInfoAction = {
  meta: { arg: { body: ICartContactInfo } };
};

const redactBindContactInfoArg = (action: UnknownAction): UnknownAction => {
  const typed = action as unknown as BindContactInfoAction;
  return {
    ...action,
    meta: {
      ...typed.meta,
      arg: {
        ...typed.meta.arg,
        body: redactContactInfo(typed.meta.arg.body),
      },
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
export const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: (state: RootState) => ({
    ...state,
    user: {
      ...state.user,
      userData: hasData(state.user.userData)
        ? {
            ...state.user.userData,
            state: redactUserLayout(state.user.userData.state),
          }
        : state.user.userData,
    },
    shoppingCart: {
      ...state.shoppingCart,
      shoppingCart: redactCartStateMachine(state.shoppingCart.shoppingCart),
      newShoppingCart: redactCartStateMachine(
        state.shoppingCart.newShoppingCart,
      ),
    },
    contactInfo: {
      ...state.contactInfo,
      formContactInfo: redactContactInfo(state.contactInfo.formContactInfo),
    },
    payment: {
      ...state.payment,
      paymentSession:
        state.payment.paymentSession.status === "succeeded"
          ? {
              ...state.payment.paymentSession,
              state: redactPaymentResponse(state.payment.paymentSession.state),
            }
          : state.payment.paymentSession,
    },
  }),
  actionTransformer: (action: UnknownAction) => {
    if (setUser.match(action)) {
      return {
        ...action,
        payload: redactUserLayout(action.payload),
      };
    }

    if (logInWithID.fulfilled.match(action)) {
      return redactUserLayoutPayload(action);
    }

    if (setFormContactInfo.match(action)) {
      return {
        ...action,
        payload: redactContactInfo(action.payload),
      };
    }

    if (
      bindContactInfo.pending.match(action) ||
      bindContactInfo.fulfilled.match(action) ||
      bindContactInfo.rejected.match(action)
    ) {
      return redactBindContactInfoArg(action);
    }

    if (
      initializePayment.pending.match(action) ||
      initializePayment.fulfilled.match(action) ||
      initializePayment.rejected.match(action)
    ) {
      return {
        ...action,
        ...(initializePayment.fulfilled.match(action) && {
          payload: redactPaymentResponse(action.payload),
        }),
        meta: {
          ...action.meta,
          arg: {
            ...action.meta.arg,
            confirmationEmail: email(action.meta.arg.confirmationEmail),
            customerGUID: REDACTED,
          },
        },
      };
    }

    if (
      updateCustomerCart.pending.match(action) ||
      updateCustomerCart.fulfilled.match(action) ||
      updateCustomerCart.rejected.match(action)
    ) {
      const typedAction = action as unknown as {
        meta: { arg: { body: { CustomerPersonID: number } } };
      };

      return {
        ...action,
        ...(updateCustomerCart.fulfilled.match(action) && {
          payload: redactCartLayout(action.payload),
        }),
        meta: {
          ...typedAction.meta,
          arg: {
            ...typedAction.meta.arg,
            body: {
              ...typedAction.meta.arg.body,
              CustomerPersonID: 0,
              LoyaltyProgramMembershipID: 0,
            },
          },
        },
      };
    }

    if (
      refreshCart.fulfilled.match(action) ||
      removeCustomerFromCart.fulfilled.match(action) ||
      updateTheatreResource.fulfilled.match(action) ||
      initializeDiningCart.fulfilled.match(action) ||
      bundleShoppingCart.fulfilled.match(action) ||
      unbundleShoppingCart.fulfilled.match(action)
    ) {
      return redactCartLayoutPayload(action);
    }

    return action;
  },
  configureScopeWithState: (scope, state: RootState) => {
    scope.setUser({
      id: hasData(state.user.userData)
        ? state.user.userData.state.ID
        : undefined,
    });
  },
});
